/**
 * Manage Regions Modal Component
 * Modal for managing allowed/blocked countries
 */

import React, { useState, useMemo } from 'react';
import { allCountries } from '../../data/countries';
import './ManageRegionsModal.css';

interface ManageRegionsModalProps {
  isOpen: boolean;
  allowedRegions: string[];
  onClose: () => void;
  onSave: (allowedRegions: string[]) => void;
}

const ManageRegionsModal: React.FC<ManageRegionsModalProps> = ({
  isOpen,
  allowedRegions,
  onClose,
  onSave,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [tempAllowedRegions, setTempAllowedRegions] = useState<string[]>(allowedRegions);

  // Update temp state when props change
  React.useEffect(() => {
    if (isOpen) {
      setTempAllowedRegions(allowedRegions);
      setSearchQuery('');
    }
  }, [isOpen, allowedRegions]);

  const handleToggleCountry = (countryCode: string) => {
    if (tempAllowedRegions.includes(countryCode)) {
      // Remove from allowed
      setTempAllowedRegions(tempAllowedRegions.filter(code => code !== countryCode));
    } else {
      // Add to allowed
      setTempAllowedRegions([...tempAllowedRegions, countryCode]);
    }
  };

  const handleSave = () => {
    onSave(tempAllowedRegions);
    onClose();
  };

  const handleCancel = () => {
    setTempAllowedRegions(allowedRegions);
    setSearchQuery('');
    onClose();
  };

  // Filter countries by search query
  const filteredCountries = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    if (!query) return allCountries;
    
    return allCountries.filter(country =>
      country.name.toLowerCase().includes(query) ||
      country.code.toLowerCase().includes(query)
    );
  }, [searchQuery]);

  // Separate allowed and not allowed countries
  const allowedCountries = useMemo(() => {
    return filteredCountries.filter(country => tempAllowedRegions.includes(country.code));
  }, [filteredCountries, tempAllowedRegions]);

  const notAllowedCountries = useMemo(() => {
    return filteredCountries.filter(country => !tempAllowedRegions.includes(country.code));
  }, [filteredCountries, tempAllowedRegions]);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleCancel}>
      <div className="modal-content manage-regions-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>🌍 Manage Regions</h2>
          <button className="modal-close" onClick={handleCancel}>✕</button>
        </div>

        <div className="modal-body manage-regions-body">
          {/* Search */}
          <div className="manage-regions-search">
            <input
              type="text"
              className="form-input"
              placeholder="Search countries..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {/* Scrollable Container */}
          <div className="regions-scrollable">
            {/* Allowed Regions Section - Sticky */}
            <div className="allowed-regions-section sticky-section">
              <div className="section-header">
                <span className="section-title">Allowed Regions ({allowedCountries.length})</span>
              </div>
              <div className="countries-list allowed-list">
                {allowedCountries.length > 0 ? (
                  allowedCountries.map((country) => (
                    <div
                      key={country.code}
                      className="country-item allowed"
                      onClick={() => handleToggleCountry(country.code)}
                    >
                      <span className="country-flag">{country.flag}</span>
                      <span className="country-name">{country.name}</span>
                      <span className="country-check">✓</span>
                    </div>
                  ))
                ) : (
                  <div className="empty-state">No allowed regions</div>
                )}
              </div>
            </div>

            {/* Divider */}
            <div className="regions-divider"></div>

            {/* Not Allowed Regions Section */}
            <div className="not-allowed-regions-section">
              <div className="section-header">
                <span className="section-title">All Regions ({notAllowedCountries.length})</span>
              </div>
              <div className="countries-list">
                {notAllowedCountries.length > 0 ? (
                  notAllowedCountries.map((country) => (
                    <div
                      key={country.code}
                      className="country-item"
                      onClick={() => handleToggleCountry(country.code)}
                    >
                      <span className="country-flag">{country.flag}</span>
                      <span className="country-name">{country.name}</span>
                    </div>
                  ))
                ) : (
                  <div className="empty-state">All regions are allowed</div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={handleCancel}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleSave}>
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

export default ManageRegionsModal;
